import {
  useTanstackSheetsFetchFn,
  useTanstackSpotsFetchFn,
  useTanstackUserStampsFetchFn,
} from '@/core/api/useTanstackFetchFn'
import { isValidCampaignSlug, useCampaignSlug } from '@/core/app/useCampaignSlug'
import { resolveSelectedSheet } from '@/domain/spot/resolveSelectedSheet'
import type { UserStampListResponse } from '@/schemas/v2'
import type { SpotData, SpotTagData } from '@/schemas/v2/spot'
import { useSpotSheetSelectionStore } from '@/store/useSpotSheetSelectionStore'
import { useQuery } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useCallback, useMemo } from 'react'
import { assign, setup } from 'xstate'

// ---------------------------------------------------------------------------
// Context / Event 型
// ---------------------------------------------------------------------------

type SpotUiContext = {
  /** 選択中のタグ ID（未選択 = null） */
  selectedTagId: number | null
  /** PDF ダイアログ表示中か */
  showPdfDialog: boolean
  /** ARフォトオーバーレイに表示するスポット（null = 非表示）*/
  arPhotoSpot: SpotData | null
}

type SpotUiEvent =
  /** PDF ダイアログを開く */
  | { type: 'SHOW_PDF' }
  /** PDF ダイアログを閉じる */
  | { type: 'CLOSE_PDF' }
  /** タグフィルタをトグル（同じ ID を選択すると解除） */
  | { type: 'SELECT_TAG'; tagId: number }
  /** ARフォトオーバーレイを開く */
  | { type: 'OPEN_AR_PHOTO'; spot: SpotData }
  /** ARフォトオーバーレイを閉じる */
  | { type: 'CLOSE_AR_PHOTO' }

// ---------------------------------------------------------------------------
// 内部ヘルパー
// ---------------------------------------------------------------------------

const deriveUniqueTags = (spots: SpotData[]): SpotTagData[] => {
  const seen = new Set<number>()
  const result: SpotTagData[] = []
  for (const spot of spots) {
    for (const tag of spot.tags) {
      if (!seen.has(tag.id)) {
        seen.add(tag.id)
        result.push(tag)
      }
    }
  }
  return result
}

const applyFilter = (spots: SpotData[], selectedSheet: string, selectedTagId: number | null): SpotData[] =>
  spots.filter((spot) => {
    // selectedSheet はシートIDの文字列（'' = すべてのコース）
    const matchesSheet = selectedSheet === '' || spot.sheetIds.some((id) => String(id) === selectedSheet)
    const matchesTag = selectedTagId === null || spot.tags.some((t) => t.id === selectedTagId)
    return matchesSheet && matchesTag
  })

// ---------------------------------------------------------------------------
// ヘルパー: spotId → stampImage マップを構築
// ---------------------------------------------------------------------------

function buildStampImageBySpotId(stamps: UserStampListResponse['stamps']): Map<number, string> {
  const map = new Map<number, string>()
  for (const stamp of stamps) {
    if (!stamp.isAcquired || !stamp.image) continue
    for (const spot of stamp.spots) {
      // 1つのスポットに複数スタンプが紐づく場合（#2540）は先勝ち。
      // GET /api/v2/user/stamps はスタンプ ID 昇順で返るため（findActivesByCampaignWithSpotsOrderById）、
      // 「獲得済みのうちスタンプ ID 昇順で先頭」の画像が採用される。
      // スポット詳細のオーバーレイ（detail.tsx の stampImageUrl）も同じ基準にそろえている。
      if (!map.has(spot.id)) {
        map.set(spot.id, stamp.image)
      }
    }
  }
  return map
}

// ---------------------------------------------------------------------------
// ヘルパー: 獲得済みスポット ID セットを構築
// buildStampImageBySpotId は stamp.image が null のスタンプを除外するため、
// ポイントラリーのように画像がないスタンプの獲得判定には利用できない。
// ---------------------------------------------------------------------------

function buildAcquiredSpotIds(stamps: UserStampListResponse['stamps']): Set<number> {
  const set = new Set<number>()
  for (const stamp of stamps) {
    if (!stamp.isAcquired) continue
    for (const spot of stamp.spots) {
      set.add(spot.id)
    }
  }
  return set
}

// ---------------------------------------------------------------------------
// UI ステートマシン（データ取得は useQuery に委譲）
// コース選択はページをまたいで保持するため、マシンではなく useSpotSheetSelectionStore で持つ（#3334）
// マップ / リストのタブは履歴エントリごとに復元するため、マシンではなく URL クエリ ?tab= で持つ（#3665）
// ---------------------------------------------------------------------------

const spotUiMachine = setup({
  types: {
    context: {} as SpotUiContext,
    events: {} as SpotUiEvent,
  },
}).createMachine({
  id: 'spotUi',
  context: {
    selectedTagId: null,
    showPdfDialog: false,
    arPhotoSpot: null,
  },
  on: {
    SHOW_PDF: { actions: assign({ showPdfDialog: true }) },
    CLOSE_PDF: { actions: assign({ showPdfDialog: false }) },
    OPEN_AR_PHOTO: { actions: assign({ arPhotoSpot: ({ event }) => event.spot }) },
    CLOSE_AR_PHOTO: { actions: assign({ arPhotoSpot: null }) },
    SELECT_TAG: {
      actions: assign({
        selectedTagId: ({ context, event }) => (context.selectedTagId === event.tagId ? null : event.tagId),
      }),
    },
  },
})

// ---------------------------------------------------------------------------
// 公開フック
// ---------------------------------------------------------------------------

export const useSpotsFetchStateMachine = ({
  isAuthenticated,
  defaultSheetId,
}: {
  isAuthenticated: boolean
  /** キャンペーン設定の初期シートID（未設定 = null） */
  defaultSheetId: number | null
}) => {
  const spotsFetch = useTanstackSpotsFetchFn()
  const { data, isLoading } = useQuery(spotsFetch)
  const [state, send] = useMachine(spotUiMachine)

  const userStampsFetch = useTanstackUserStampsFetchFn()
  const { data: userStampsData } = useQuery({
    ...userStampsFetch,
    enabled: isAuthenticated,
  })

  // シート一覧取得（point_rally 判定用）
  const sheetsFetch = useTanstackSheetsFetchFn()
  const { data: sheetsData } = useQuery(sheetsFetch)

  const spots = data?.spots ?? []
  // 一覧レスポンスのシート情報（ドロップダウン用。名前・ロック・詳細公開フラグを持つ）
  const listSheets = data?.sheets ?? []

  // シートID → 表示名（バッジ表示用）。購入連動ロック中（locked=true）は 🔓 を付与し、
  // フィルタのドロップダウン（sheetOptions）と表示を揃える。
  const sheetNameById = useMemo<Map<number, string>>(() => {
    const map = new Map<number, string>()
    for (const sheet of listSheets) map.set(sheet.id, sheet.locked ? `🔓 ${sheet.name}` : sheet.name)
    return map
  }, [listSheets])

  // フィルタのドロップダウン候補: 実際にスポットが属するシートのみ（一覧の表示順を維持）
  const sheetFilterOptions = useMemo<{ id: number; name: string; locked: boolean }[]>(() => {
    const present = new Set<number>()
    for (const spot of spots) for (const id of spot.sheetIds) present.add(id)
    return listSheets
      .filter((sheet) => present.has(sheet.id))
      .map((sheet) => ({ id: sheet.id, name: sheet.name, locked: sheet.locked }))
  }, [spots, listSheets])

  // 表示するコース: ユーザーの選択 → 初期シート → すべて の順（#3334）。
  // campaignData と一覧が別々に届いても結果が変わらないよう、state に書き込まず描画時に導出する
  const { campaignSlug } = useCampaignSlug()
  const slug = isValidCampaignSlug(campaignSlug) ? campaignSlug : null
  const userSelection = useSpotSheetSelectionStore((s) =>
    slug !== null ? (s.selectedSheetBySlug[slug] ?? null) : null,
  )
  const selectSheetInStore = useSpotSheetSelectionStore((s) => s.selectSheet)

  const selectedSheet = useMemo(
    () =>
      resolveSelectedSheet({
        userSelection,
        defaultSheetId,
        optionSheetIds: sheetFilterOptions.map((s) => s.id),
      }),
    [userSelection, defaultSheetId, sheetFilterOptions],
  )

  const selectSheet = useCallback(
    (sheet: string) => {
      if (slug !== null) selectSheetInStore(slug, sheet)
    },
    [slug, selectSheetInStore],
  )

  const stampImageBySpotId = useMemo<Map<number, string>>(
    () => (isAuthenticated && userStampsData ? buildStampImageBySpotId(userStampsData.stamps) : new Map()),
    [isAuthenticated, userStampsData],
  )

  // 獲得済みスポット ID セット（stamp.image が null でも追跡）
  const acquiredSpotIds = useMemo<Set<number>>(
    () => (isAuthenticated && userStampsData ? buildAcquiredSpotIds(userStampsData.stamps) : new Set<number>()),
    [isAuthenticated, userStampsData],
  )

  // キャンペーンに point_rally シートが 1 件以上あるか
  const isPointRally = useMemo<boolean>(
    () => (sheetsData?.sheets ?? []).some((s) => s.type === 'point_rally'),
    [sheetsData],
  )

  return {
    send,
    selectSheet,
    isLoading,
    sheetNameById,
    context: {
      ...state.context,
      selectedSheet,
      spots,
      uniqueTags: deriveUniqueTags(spots),
      sheetFilterOptions,
      filteredSpots: applyFilter(spots, selectedSheet, state.context.selectedTagId),
      stampImageBySpotId,
      acquiredSpotIds,
      isPointRally,
    },
  }
}
